import React, { useEffect, useState } from 'react';

import { css, styled } from '@mui/material';
import { useTranslation } from '@kc-console/core';
import { IcCopyRegular } from '@kep/kcds-icons';

import { useCopyToClipboard } from '../../utils';
import { buttonClasses } from '../Button';
import { IconButtonGhost } from '../IconButtonGhost';
import { Tooltip } from '../Tooltip';
import { copyButtonClasses } from './theme';

import type { MouseEvent } from 'react';
import type { CopyButtonOwnerState, CopyButtonProps } from './types';

export default function CopyButton(props: CopyButtonProps) {
  const { t } = useTranslation();
  const {
    value,
    messageAfterCopy = t('ui:action.copy_success'),
    messageBeforeCopy,
    timeout = 1000,
    onClick,
    onMouseEnter: onMouseEnterProp,
    onMouseLeave: onMouseLeaveProp,
    size = 'sm',
    slotProps,
    ...others
  } = props;
  const { tooltip: tooltipProps } = slotProps ?? {};

  const ownerState: CopyButtonOwnerState = {
    ...props,
    messageAfterCopy,
    timeout,
    size,
  };

  const [{ open, copied, message }, setState] = useState({
    open: false,
    copied: false,
    message: messageBeforeCopy,
  });

  const { copyToClipboard, setCopyState } = useCopyToClipboard({ value });

  const onClickCopyButton = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) {
      copyToClipboard();
      setState(prev => ({ ...prev, copied: true, message: messageAfterCopy }));
    }
  };

  const onMouseEnter = (event: MouseEvent<HTMLButtonElement>) => {
    setState(prev => ({
      ...prev,
      open: true,
      message: copied ? messageAfterCopy : messageBeforeCopy,
    }));
    onMouseEnterProp?.(event);
  };
  const onMouseLeave = (event: MouseEvent<HTMLButtonElement>) => {
    setState(prev => ({ ...prev, open: false }));
    onMouseLeaveProp?.(event);
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        // Tooltip fade-out 애니메이션 중에 title이 교체되면 중첩 노출처럼 보이므로
        // message는 유지한 채 open/copied만 해제하고, 다음 onMouseEnter 시 messageBeforeCopy로 초기화한다.
        setState(prev => ({ ...prev, open: false, copied: false }));
        setCopyState({ value: null, error: null });
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  return (
    <CopyButtonTooltip
      open={open || copied}
      message={message}
      ownerState={ownerState}
      {...tooltipProps}
    >
      <CopyButtonRoot
        size={size}
        aria-label={t('ui:action.copy')}
        onClick={onClickCopyButton}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ownerState={ownerState}
        {...others}
      >
        <IcCopyRegular />
      </CopyButtonRoot>
    </CopyButtonTooltip>
  );
}

const CopyButtonRoot = styled(IconButtonGhost, {
  target: copyButtonClasses.root,
})<{ ownerState: CopyButtonOwnerState }>(
  ({ theme }) => css`
    width: fit-content;
    min-width: initial;
    height: fit-content;
    padding: 0px;
    color: ${theme.color['icon/weakest/default']};
    &:hover {
      color: ${theme.color['icon/weakest/hover']};
      background: initial;
    }
    & > .${buttonClasses.label} > svg {
      display: block;
    }
  `,
);

const CopyButtonTooltip = styled(Tooltip, {
  target: copyButtonClasses.tooltip,
})<{ ownerState: CopyButtonOwnerState }>``;
